import type { UpdateOrgSettings } from '@sdk/authentication/types/OrganisationType';
import getNowDate from '@sdk/utils/getNowDate';
import getRealDate from '@sdk/utils/getRealDate';
import { ClockInGeoMode } from '@sdk/authentication/types';
import { PTO_FIXED_DATE_TYPE } from '@sdk/common/constants';

import {
  MULTI_LOCATION_OTHER_SCHEDULES_OPTIONS,
  SINGLE_LOCATION_OTHER_SCHEDULES_OPTIONS,
} from '../constants';
import {
  TimeClockGeofencingModesOption,
  VisibilitySchedulesOptions,
  VisibilitySchedulesOptionType,
} from '../types';

type getUsersSeeOtherSchedulesOptionType = {
  orgLocationsCount: number;
  canSeeOtherLocations: boolean;
  canSeeOtherPositions: boolean;
};
export const getUsersSeeOtherSchedulesOption = ({
  orgLocationsCount,
  canSeeOtherLocations,
  canSeeOtherPositions,
}: getUsersSeeOtherSchedulesOptionType): VisibilitySchedulesOptions => {
  if (orgLocationsCount === 1 && canSeeOtherPositions) {
    return VisibilitySchedulesOptions.accessPositionsEnabled;
  }
  if (orgLocationsCount === 1 && !canSeeOtherPositions) {
    return VisibilitySchedulesOptions.accessPositionsDisabled;
  }
  if (canSeeOtherPositions && canSeeOtherLocations) {
    return VisibilitySchedulesOptions.accessPositionsEnabledLocationsEnabled;
  }
  if (!canSeeOtherPositions && canSeeOtherLocations) {
    return VisibilitySchedulesOptions.accessPositionsDisabledLocationsEnabled;
  }
  if (canSeeOtherPositions && !canSeeOtherLocations) {
    return VisibilitySchedulesOptions.accessPositionsEnabledLocationsDisabled;
  }
  if (!canSeeOtherPositions && !canSeeOtherLocations) {
    return VisibilitySchedulesOptions.accessPositionsDisabledLocationsDisabled;
  }
};

export const getUsersSeeOtherSchedulesOptions = (
  orgLocationsCount: number,
  usersCanSeeUsersOutsideLocation: boolean
): VisibilitySchedulesOptionType[] => {
  if (orgLocationsCount === 1) {
    return SINGLE_LOCATION_OTHER_SCHEDULES_OPTIONS;
  }
  if (!usersCanSeeUsersOutsideLocation) {
    return MULTI_LOCATION_OTHER_SCHEDULES_OPTIONS.filter(
      (option) =>
        option.value === VisibilitySchedulesOptions.accessPositionsEnabledLocationsDisabled ||
        option.value === VisibilitySchedulesOptions.accessPositionsDisabledLocationsDisabled
    );
  }
  return MULTI_LOCATION_OTHER_SCHEDULES_OPTIONS;
};

export const setUsersSeeOtherSchedulesOption = (
  value: VisibilitySchedulesOptions,
  onSettingsChange: UpdateOrgSettings
): void => {
  switch (value) {
    case VisibilitySchedulesOptions.accessPositionsEnabled:
      onSettingsChange({ usersCanSeeSchedulesForOtherPositions: true });
      break;
    case VisibilitySchedulesOptions.accessPositionsDisabled:
      onSettingsChange({ usersCanSeeSchedulesForOtherPositions: false });
      break;
    case VisibilitySchedulesOptions.accessPositionsEnabledLocationsEnabled:
      onSettingsChange({
        usersCanSeeSchedulesForOtherPositions: true,
        usersCanSeeSchedulesInOtherLocations: true,
      });
      break;
    case VisibilitySchedulesOptions.accessPositionsEnabledLocationsDisabled:
      onSettingsChange({
        usersCanSeeSchedulesForOtherPositions: true,
        usersCanSeeSchedulesInOtherLocations: false,
      });
      break;
    case VisibilitySchedulesOptions.accessPositionsDisabledLocationsEnabled:
      onSettingsChange({
        usersCanSeeSchedulesForOtherPositions: false,
        usersCanSeeSchedulesInOtherLocations: true,
      });
      break;
    case VisibilitySchedulesOptions.accessPositionsDisabledLocationsDisabled:
      onSettingsChange({
        usersCanSeeSchedulesForOtherPositions: false,
        usersCanSeeSchedulesInOtherLocations: false,
      });
      break;

    default:
      break;
  }
};

export const handleGeofencingModesChange = (
  geofencingModesOptions: TimeClockGeofencingModesOption[]
): ClockInGeoMode | false => {
  const selectedOptions = geofencingModesOptions.filter((item) => item.value);
  if (selectedOptions.length === 1) {
    const [option] = selectedOptions;
    return option.name;
  }
  if (selectedOptions.length === 2) {
    return ClockInGeoMode.both;
  }

  return false;
};

const getDefaultLeaveResetDate = (): Date => {
  const date = getNowDate();
  date.setMonth(0);
  date.setDate(1);

  return date;
};

export const getLeaveResetDate = (leaveResetDate?: string): Date => {
  return leaveResetDate ? getRealDate(leaveResetDate) : getDefaultLeaveResetDate();
};

export const getNumberFromBoolean = (value: boolean): number => (value ? 1 : 0);

export const isLeaveResetTypeFixed = (leaveResetType: string): boolean => {
  return leaveResetType === PTO_FIXED_DATE_TYPE;
};
